import axios from 'axios';
import mockApiService from './mockApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const USE_MOCK_API = false; // Set to false when backend is available

class ApiService {
  constructor() {
    // Create axios instance with base configuration
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/api`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    // Add auth token to requests if available
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.reload();
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async verifyGoogleToken(token) {
    const response = await this.api.post('/auth/google', { token });
    return response.data;
  }

  // Client operations
  async getClients() {
    if (USE_MOCK_API) return mockApiService.getClients();
    const response = await this.api.get('/clients');
    return response.data;
  }

  async createClient(name) {
    if (USE_MOCK_API) return mockApiService.createClient(name);
    const response = await this.api.post('/clients', { name });
    return response.data;
  }

  async checkSimilarClients(name) {
    if (USE_MOCK_API) return { suggestions: [], hasSuggestions: false };
    const response = await this.api.get(`/clients/check-similar/${encodeURIComponent(name)}`);
    return response.data;
  }

  // Job operations
  async getJobs(clientName) {
    if (USE_MOCK_API) return mockApiService.getJobs(clientName);
    const response = await this.api.get(`/clients/${encodeURIComponent(clientName)}/jobs`);
    return response.data;
  }

  async createJob(clientName, jobData) {
    if (USE_MOCK_API) return mockApiService.createJob(clientName, jobData);
    const response = await this.api.post(`/clients/${encodeURIComponent(clientName)}/jobs`, { name: jobData });
    return response.data;
  }

  // Sequence operations
  async getSequences(clientName, jobName) {
    if (USE_MOCK_API) return mockApiService.getSequences(clientName, jobName);
    const response = await this.api.get(
      `/clients/${encodeURIComponent(clientName)}/jobs/${encodeURIComponent(jobName)}/sequences`
    );
    return response.data;
  }

  async createSequence(clientName, jobName, sequenceData) {
    if (USE_MOCK_API) return mockApiService.createSequence(clientName, jobName, sequenceData);
    const response = await this.api.post(
      `/clients/${encodeURIComponent(clientName)}/jobs/${encodeURIComponent(jobName)}/sequences`,
      { name: sequenceData }
    );
    return response.data;
  }

  // Shot operations
  async getShots(clientName, jobName, sequenceName) {
    if (USE_MOCK_API) return mockApiService.getShots(clientName, jobName, sequenceName);
    const response = await this.api.get(
      `/clients/${encodeURIComponent(clientName)}/jobs/${encodeURIComponent(jobName)}/sequences/${encodeURIComponent(sequenceName)}/shots`
    );
    return response.data;
  }

  async createShot(clientName, jobName, sequenceName, shotData, increment = null) {
    if (USE_MOCK_API) return mockApiService.createShot(clientName, jobName, sequenceName, shotData, increment);
    const response = await this.api.post(
      `/clients/${encodeURIComponent(clientName)}/jobs/${encodeURIComponent(jobName)}/sequences/${encodeURIComponent(sequenceName)}/shots`,
      { shotData, increment }
    );
    return response.data;
  }

  // Application folder operations
  async getExistingApplications(clientName, jobName, sequenceName, shotName) {
    if (USE_MOCK_API) return { applications: [] }; // Mock response
    const response = await this.api.get(
      `/clients/${encodeURIComponent(clientName)}/jobs/${encodeURIComponent(jobName)}/sequences/${encodeURIComponent(sequenceName)}/shots/${encodeURIComponent(shotName)}/applications`
    );
    return response.data;
  }

  async checkApplicationFolders(clientName, jobName, sequenceName, shotName, applications) {
    if (USE_MOCK_API) return { exists: {}, hasAnyExisting: false }; // Mock response
    const response = await this.api.get(
      `/clients/${encodeURIComponent(clientName)}/jobs/${encodeURIComponent(jobName)}/sequences/${encodeURIComponent(sequenceName)}/shots/${encodeURIComponent(shotName)}/applications/check?applications=${applications.join(',')}`
    );
    return response.data;
  }

  async addApplicationFolders(clientName, jobName, sequenceName, shotName, applications) {
    if (USE_MOCK_API) return mockApiService.addApplicationFolders(clientName, jobName, sequenceName, shotName, applications);
    const response = await this.api.post(
      `/clients/${encodeURIComponent(clientName)}/jobs/${encodeURIComponent(jobName)}/sequences/${encodeURIComponent(sequenceName)}/shots/${encodeURIComponent(shotName)}/applications`,
      { applications }
    );
    return response.data;
  }

  // Combined structure creation
  async createStructure(structure) {
    if (USE_MOCK_API) return mockApiService.createStructure(structure);
    const response = await this.api.post('/structure', structure);
    return response.data;
  }

  // Batch structure creation
  async createBatch(operations) {
    const response = await this.api.post('/batch', { operations });
    return response.data;
  }

  // Utility methods
  async validateJobNumber(jobNumber) {
    const response = await this.api.get(`/validate/job-number/${encodeURIComponent(jobNumber)}`);
    return response.data;
  }

  async validateSequenceName(sequenceName) {
    const response = await this.api.get(`/validate/sequence-name/${encodeURIComponent(sequenceName)}`);
    return response.data;
  }

  async getJobStructure(clientName, jobName) {
    const response = await this.api.get(
      `/clients/${encodeURIComponent(clientName)}/jobs/${encodeURIComponent(jobName)}/structure`
    );
    return response.data;
  }

  async getQueuedItems(clientName, jobName) {
    const response = await this.api.get(
      `/clients/${encodeURIComponent(clientName)}/jobs/${encodeURIComponent(jobName)}/queued`
    );
    return response.data;
  }

  async clearQueuedItems(clientName, jobName) {
    const response = await this.api.delete(
      `/clients/${encodeURIComponent(clientName)}/jobs/${encodeURIComponent(jobName)}/queued`
    );
    return response.data;
  }

  // Template operations
  async getAvailableTemplates() {
    const response = await this.api.get('/templates');
    return response.data;
  }

  async getApplicationTemplates() {
    const response = await this.api.get('/templates/applications');
    return response.data;
  }

  // Get full hierarchy in one request
  async getHierarchy() {
    if (USE_MOCK_API) return { hierarchy: {} };
    const response = await this.api.get('/structure/hierarchy');
    return response.data;
  }

  // Logs operations
  async get(endpoint) {
    const response = await this.api.get(endpoint);
    return response;
  }
}

const apiService = new ApiService();
export default apiService;